import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface MarkdownMessageProps {
  message: Message;
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ message }) => {
  // Strip source citations like [1], [5], [1, 2], etc. to keep the UI clean
  const cleanContent = message.role === 'assistant'
    ? message.content.replace(/\[\d+\]/g, '')
    : message.content;

  return (
    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-2xl rounded-lg p-4 ${message.role === 'user'
          ? 'bg-gradient-to-r from-secondary to-primary text-dark font-bold shadow-lg shadow-primary/20'
          : 'bg-dark/80 backdrop-blur-sm text-gray-200 border border-slate/30'
          }`}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ ...props }) => <h1 className="text-2xl font-bold text-primary mb-3" {...props} />,
            h2: ({ ...props }) => <h2 className="text-xl font-bold text-primary mb-2" {...props} />,
            h3: ({ ...props }) => <h3 className="text-lg font-bold text-accent mb-2" {...props} />,
            strong: ({ ...props }) => <strong className="font-bold text-primary/90" {...props} />,
            em: ({ ...props }) => <em className="italic text-accent" {...props} />,
            code: ({ inline, ...props }: any) =>
              inline ? (
                <code className="bg-dark text-accent px-1 rounded" {...props} />
              ) : (
                <code className="block bg-dark text-primary p-2 rounded my-2 overflow-x-auto border border-slate/30" {...props} />
              ),
            ul: ({ ...props }) => <ul className="list-disc list-inside my-2 space-y-1" {...props} />,
            ol: ({ ...props }) => <ol className="list-decimal list-inside my-2 space-y-1" {...props} />,
            li: ({ ...props }) => <li className="text-gray-300 ml-4" {...props} />,
            a: ({ ...props }) => <a className="text-primary hover:text-accent underline" {...props} />,
            blockquote: ({ ...props }) => (
              <blockquote className="border-l-4 border-secondary pl-4 italic text-gray-400 my-2" {...props} />
            ),
          }}
        >
          {cleanContent}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownMessage;
